import { TFunction } from 'i18next'

import { ChipProps } from '@forto-dev/phoenix-ui-core'

import {
  AuthorizedDTO,
  BookingDTOState,
  ExplicitlyApprovedByControllingPartyDTO,
  ExplicitlyApprovedByControllingPartyDTOType,
  GetHistoryDTOItemsInner,
  RejectedDTO,
} from '@/clients/booking'
import { useLocalisation } from '@/utils/localisation'

export const mapBookingStateToChipVariant = (state: BookingDTOState | 'shipmentCreated'): ChipProps['variant'] => {
  const mapStateToChipVariant: Record<BookingDTOState | 'shipmentCreated', ChipProps['variant']> = {
    [BookingDTOState.Open]: 'primary',
    [BookingDTOState.Nominated]: 'primary',
    [BookingDTOState.CarrierContacted]: 'primary',
    [BookingDTOState.Confirmed]: 'primary',
    [BookingDTOState.Authorized]: 'positive',
    [BookingDTOState.Canceled]: 'neutral',
    [BookingDTOState.Rejected]: 'negative',
    shipmentCreated: 'positive',
  }

  return mapStateToChipVariant[state]
}

export const mapBookingStateToTranslation = (
  state: BookingDTOState | 'shipmentCreated',
  translate: TFunction
): string => {
  const mapBookingStateToTranslation: Record<BookingDTOState | 'shipmentCreated', string> = {
    [BookingDTOState.Open]: translate('booking.overview.state_open'),
    [BookingDTOState.Nominated]: translate('booking.overview.state_nominated'),
    [BookingDTOState.CarrierContacted]: translate('booking.overview.state_carrier-contacted'),
    [BookingDTOState.Confirmed]: translate('booking.overview.state_confirmed'),
    [BookingDTOState.Authorized]: translate('booking.overview.state_authorized'),
    [BookingDTOState.Canceled]: translate('booking.overview.state_canceled'),
    [BookingDTOState.Rejected]: translate('booking.overview.state_rejected'),
    shipmentCreated: translate('integrations.subscriptions.types.shipmentCreated'),
  }

  return mapBookingStateToTranslation[state]
}

type BookingHistoryEventWithComment = AuthorizedDTO | RejectedDTO | ExplicitlyApprovedByControllingPartyDTO
export const hasComment = (historyItem?: GetHistoryDTOItemsInner): historyItem is BookingHistoryEventWithComment => {
  return (historyItem as BookingHistoryEventWithComment)?.comment !== undefined
}

type GetBookingStateCommentArgs = {
  historyItems?: GetHistoryDTOItemsInner[]
  state: BookingDTOState
  translateDate: ReturnType<typeof useLocalisation>['translateDate']
  translate: TFunction
}
export const getBookingStateComment = ({
  historyItems = [],
  state,
  translateDate,
  translate,
}: GetBookingStateCommentArgs): string | undefined => {
  const latestHistoryItem = historyItems.find((item) => item.type === state)

  let explicitlyApprovedByControllingPartyEvent: ExplicitlyApprovedByControllingPartyDTO | undefined

  // We want to display the comment of the explicitly approved by controlling party event if the booking is in authorized state only
  if (state === BookingDTOState.Authorized) {
    explicitlyApprovedByControllingPartyEvent = historyItems.find(
      (item) => item.type === ExplicitlyApprovedByControllingPartyDTOType.ExplicitlyApprovedByControllingParty
    )
  }

  const historyItem = explicitlyApprovedByControllingPartyEvent ?? latestHistoryItem

  return hasComment(historyItem)
    ? `${historyItem.comment} ${translate('booking.overview.on')} ${translateDate('booking_action_date', new Date(historyItem.at))}`
    : undefined
}
